import axios from 'axios';

const PROD_API_URL = 'https://backend.xsignature.ruddypp.site';
const API_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? PROD_API_URL : '');

const normalizeBaseUrl = (value) => (value || '').replace(/\/+$/, '');
const shouldRetryWithProdApi = (baseURL = '') => {
  const normalizedBaseUrl = normalizeBaseUrl(baseURL);

  return normalizedBaseUrl !== normalizeBaseUrl(PROD_API_URL);
};

const API = axios.create({
  baseURL: API_URL,
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 15000
});

const refreshToken = async () => {
  const token = localStorage.getItem('token');
  if (!token) {
    localStorage.removeItem('token');
    throw new Error('Token refresh failed');
  }

  const refreshAPI = axios.create({
    baseURL: API_URL,
    timeout: 15000
  });

  await refreshAPI.get('/api/auth/verify-token', {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  return token;
};

API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    const isAuthRoute = config.url?.includes('/api/auth/');

    // Auth routes do not require a bearer token and skipping it avoids
    // unnecessary preflight headers during login/register.
    if (token && !isAuthRoute) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const isNetworkError = !error.response && !!originalRequest;
    const isAuthRoute = originalRequest?.url?.includes('/api/auth/');
    const isAuthError =
      status === 401 ||
      (status === 403 && error.response?.data?.message === 'Invalid or expired token');

    // If the app is still pointing to an outdated dev/local URL, retry once
    // against the canonical hosted backend before surfacing the error.
    if (isNetworkError && !originalRequest?._networkRetry && shouldRetryWithProdApi(originalRequest?.baseURL)) {
      originalRequest._networkRetry = true;
      originalRequest.baseURL = PROD_API_URL;
      return API(originalRequest);
    }

    if (isAuthError && !isAuthRoute && !originalRequest?._retry) {
      originalRequest._retry = true;

      try {
        const token = await refreshToken();
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return API(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('token');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    if (isAuthError && isAuthRoute) {
      localStorage.removeItem('token');
    }

    return Promise.reject(error);
  }
);

export default API;
